import { type RefObject } from 'react'
import { Image, FileCode, Copy } from 'lucide-react'
import { toast } from 'sonner'
import type { QRPreviewRef } from './QRPreview'
import type { QRSettings } from '../../types/qr'
import './QRDownload.scss'

interface QRDownloadProps {
  qrRef: RefObject<QRPreviewRef | null>
  settings: QRSettings
}

export function QRDownload({ qrRef, settings }: QRDownloadProps) {
  const getFilename = () => {
    if (!settings.value) return 'qrcode'
    try {
      const url = new URL(settings.value)
      return url.hostname.replace(/\./g, '_')
    } catch {
      return 'qrcode'
    }
  }

  const downloadPNG = () => {
    if (!qrRef.current) return
    qrRef.current.downloadPNG(getFilename())
    toast.success('PNG downloaded')
  }

  const downloadSVG = () => {
    if (!qrRef.current) return
    
    qrRef.current.downloadSVG(getFilename())
    
    if (qrRef.current.hasLogo()) {
      toast.success('SVG downloaded', {
        description: 'Note: Logo may not display correctly in some editors. Use PNG for best results with logos.',
      })
    } else {
      toast.success('SVG downloaded')
    }
  }

  const copyToClipboard = async () => {
    if (!qrRef.current) return
    
    try {
      const blob = await qrRef.current.getBlob()
      if (!blob) {
        toast.error('Failed to copy')
        return
      }

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ])
      toast.success('Copied to clipboard')
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('Failed to copy to clipboard')
    }
  }

  const isDisabled = !settings.value

  return (
    <div className="qr-download">
      <button
        className="download-btn primary full-width"
        onClick={downloadPNG}
        disabled={isDisabled}
      >
        <Image size={18} />
        Download PNG
      </button>
      <div className="download-row">
        <button
          className="download-btn secondary"
          onClick={downloadSVG}
          disabled={isDisabled}
        >
          <FileCode size={18} />
          Download SVG
        </button>
        <button
          className="download-btn secondary copy-btn"
          onClick={copyToClipboard}
          disabled={isDisabled}
          title="Copy PNG to clipboard"
        >
          <Copy size={18} />
        </button>
      </div>
    </div>
  )
}
